<template>
    <!--
        간단 안내/오류/확인 메시지용 재사용 모달.
        alert()·confirm() 대체 목적. 아이폰 홈 화면에 추가한 웹 앱에서는 브라우저 확인창이 뜨지 않고
        confirm() 이 곧바로 false 를 돌려줘 버튼이 반응 없는 것처럼 보인다.
        - ref 로 open(message[, onClose]) 호출해 띄운다(안내).
        - DefaultTemplate 이 하나 띄워 두고 this.$alert(message) / this.$confirm(message) 로 부른다(plugins/vue-message-dialog.js).
        message 의 줄바꿈(\n)은 문단으로 분리되어 표시된다.
    -->
    <v-dialog v-model="shows" width="400">
        <v-card class="common-message-dialog">
            <v-card-title v-if="heading" class="common-message-dialog__title justify-center text-center">
                {{ heading }}
            </v-card-title>
            <v-card-text class="common-message-dialog__info text-center">
                <p v-for="(line, index) in lines" :key="index">{{ line }}</p>
            </v-card-text>
            <v-card-actions class="justify-center">
                <v-btn v-if="cancelable" outlined color="grey" @click="exit"> {{ cancelText }} </v-btn>
                <v-btn color="primary" @click="accept"> {{ confirmText }} </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
export default {
    props: {
        title: { type: String, default: "안내" },
        confirmText: { type: String, default: "확인" },
        cancelText: { type: String, default: "취소" },
    },
    data: () => ({
        shows: false,
        message: "",
        heading: null,
        cancelable: false,
        accepted: false,
        onCloseHandler: null,
    }),
    computed: {
        lines() {
            return String(this.message ?? "")
                .split("\n")
                .filter((line) => line.trim().length);
        },
    },
    methods: {
        /**
         * @param {string} message 표시할 메시지 (줄바꿈 \n 지원)
         * @param {() => void} [onClose] 닫힘(확인/배경클릭 무관) 시 1회 실행할 콜백
         */
        open(message, onClose) {
            // 열려 있던 창의 onClose 는 실행하지 않고 버린다(원래 동작)
            this.onCloseHandler = null;
            this.show(message, this.title, false, typeof onClose === "function" ? () => onClose() : null);
        },
        /**
         * alert() 대체(제목 없음). 닫히면(확인/배경클릭 무관) 끝난다.
         * @returns {Promise<void>}
         */
        alert(message) {
            return new Promise((resolve) => this.show(message, null, false, () => resolve()));
        },
        /**
         * confirm() 대체(제목 없음). 확인을 누르면 true, 취소·배경클릭·ESC 는 false.
         * @returns {Promise<boolean>}
         */
        confirm(message) {
            return new Promise((resolve) => this.show(message, null, true, (accepted) => resolve(accepted)));
        },
        show(message, heading, cancelable, onClose) {
            // $alert·$confirm 이 기다리는 창이 열려 있으면 먼저 닫힌 것(취소)으로 끝낸다. 기다리는 쪽이 멈춰 있지 않게
            this.finish();
            if (message != null) this.message = message;
            this.heading = heading;
            this.cancelable = cancelable;
            this.accepted = false;
            this.onCloseHandler = onClose;
            this.shows = true;
        },
        accept() {
            this.accepted = true;
            this.shows = false;
        },
        exit() {
            this.shows = false;
        },
        finish() {
            let handler = this.onCloseHandler;
            this.onCloseHandler = null;
            if (handler) handler(this.accepted);
        },
    },
    watch: {
        // 확인 버튼·배경 클릭 등 어떤 방식으로 닫혀도 동일하게 처리한다.
        shows(value) {
            if (value) return;

            this.$emit("close");
            this.finish();
        },
    },
};
</script>

<style lang="scss" scoped>
.common-message-dialog {
    &__info {
        white-space: pre-line;

        & > p:not(:last-of-type) {
            margin-bottom: 8px;
        }
    }
}
</style>
